import { useEffect, useRef, useState } from 'react'
import { v4 } from 'uuid'

import CloseIcon from '@mui/icons-material/AddRounded'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNewRounded'

import { addKeyDownListener, removeKeyDownListener } from '@utils/listeners'

import { Portal } from '@components/Portal'

import {
  Container,
  HeaderContainer,
  ScrollContainer,
  MainContainer,
  Title,
  CloseButton,
  SubmitButton,
  PreviousButton,
} from './Modal.styled'

interface Props {
  children?: React.ReactNode
  fullScreen?: boolean

  title: string
  SubTitleComponent?: React.ReactNode
  disabledSubmitButton?: boolean

  showClose?: boolean
  onClose: () => void

  showPrevious?: boolean
  onPrevious?: () => void

  onSubmit?: () => void
  onSubmitLabel?: string

  showFooter?: boolean
  FooterComponent?: React.ReactNode

  autoHideOnSubmit?: boolean
  noValidate?: boolean

  refScrollContainer?: (ref: HTMLDivElement | null) => void
}

export const Modal = (props: Props): JSX.Element => {
  const [display, setDisplay] = useState<boolean>(false)

  useEffect(() => {
    setTimeout(() => {
      setDisplay(true)
    }, 10)
  }, [])

  const {
    children,
    title,
    showClose = true,
    disabledSubmitButton = false,
    onClose,
    onSubmit,
    refScrollContainer,
    onPrevious,
    showPrevious,
    onSubmitLabel,
    fullScreen,
    autoHideOnSubmit = true,
    noValidate = false,
  } = props

  const refComponent = useRef<string>(v4())

  useEffect(() => {
    const dismiss = (e: KeyboardEvent) => {
      if (e.key == 'Escape') {
        setDisplay(false)

        setTimeout(() => {
          onClose()
        }, 350)
      }
    }

    addKeyDownListener({ key: refComponent.current, method: dismiss })

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      removeKeyDownListener({ key: refComponent.current, method: dismiss })
    }
  }, [onClose])

  return (
    <Portal
      style={{
        top: display ? 0 : '100vh',
        left: 0,
        height: '100vh',
        width: '100vw',
        backgroundColor: 'white',
        transition: 'top 0.35s cubic-bezier(.42, 1.1, .58, 1)',
        zIndex: 1000,
      }}
    >
      <Container
        id="modal"
        noValidate={noValidate}
        onSubmit={
          onSubmit
            ? (e) => {
                e.preventDefault()
                e.stopPropagation()

                autoHideOnSubmit && setDisplay(false)

                setTimeout(() => {
                  onSubmit()
                }, 350)
              }
            : undefined
        }
      >
        <HeaderContainer>
          {showPrevious && (
            <PreviousButton onClick={onPrevious}>
              <ArrowBackIosNewIcon />
            </PreviousButton>
          )}

          {showClose && (
            <CloseButton
              onClick={() => {
                if (!showClose) return

                setDisplay(false)

                setTimeout(() => {
                  onClose()
                }, 350)
              }}
            >
              <CloseIcon />
            </CloseButton>
          )}

          <Title>{title}</Title>
          {onSubmit && onSubmitLabel ? (
            <SubmitButton disabled={disabledSubmitButton} type="submit">
              {onSubmitLabel}
            </SubmitButton>
          ) : (
            <div />
          )}
        </HeaderContainer>

        <ScrollContainer ref={refScrollContainer} open={display}>
          <MainContainer fullScreen={fullScreen}>{children}</MainContainer>
        </ScrollContainer>
      </Container>
    </Portal>
  )
}
